import type { SingleOccurrence } from '../../api/generated';
// Import the kind constant directly (not via the shared barrel) so this pure module stays free of the
// barrel's UI-component re-exports, which touch browser globals and break under the test environment.
import { SINGLE_OCCURRENCE_KIND } from '../Bookings/shared/bookingConstants';
import { FULL_DAY_END_TIME, FULL_DAY_START_TIME } from './closedDayBookingConstants';

// A LocalDateTime string looks like "2026-12-24T00:00" or "2026-12-24T23:59:00".
const DATE_LENGTH = 10; // "YYYY-MM-DD"
const TIME_START = 11;
const TIME_END = 16; // up to and excluding seconds -> "HH:mm"

// The form a Closed day is edited in. A full-day closure spans whole dates from `fromDate` to
// `toDate` (a single day when they match); a partial closure covers `fromDate` alone for the
// `startTime`–`endTime` window. `fromDate` doubles as the partial-day date, so it survives toggling.
export interface ClosedDayFormState {
  isFullDay: boolean;
  fromDate: string; // YYYY-MM-DD, also the partial-day date
  toDate: string; // YYYY-MM-DD, full-day only
  startTime: string; // HH:mm, partial-day only
  endTime: string; // HH:mm, partial-day only
}

const toLocalDateTime = (date: string, time: string): string => `${date}T${time}`;

const dateOf = (localDateTime: string): string => localDateTime.slice(0, DATE_LENGTH);
const timeOf = (localDateTime: string): string => localDateTime.slice(TIME_START, TIME_END);

/** Whether a stored window covers a whole day — start of day to the end-of-day sentinel. */
export const isFullDayWindow = (startTime: string, endTime: string): boolean =>
  startTime === FULL_DAY_START_TIME && endTime === FULL_DAY_END_TIME;

// A full-day closure: 00:00 on the first date to the end-of-day sentinel on the last. `toDate`
// defaults to `fromDate`, so a single closed day and a multi-day closure share one builder.
export const buildFullDayOccurrence = (fromDate: string, toDate: string): SingleOccurrence => ({
  kind: SINGLE_OCCURRENCE_KIND,
  startDate: toLocalDateTime(fromDate, FULL_DAY_START_TIME),
  endDate: toLocalDateTime(toDate || fromDate, FULL_DAY_END_TIME)
});

// A partial-day closure: a single date with an explicit time window.
export const buildPartialDayOccurrence = (
  date: string,
  startTime: string,
  endTime: string
): SingleOccurrence => ({
  kind: SINGLE_OCCURRENCE_KIND,
  startDate: toLocalDateTime(date, startTime),
  endDate: toLocalDateTime(date, endTime)
});

// Splits a stored Closed day back into the form's fields. Full-vs-partial is inferred from the
// times: a window from 00:00 to the end-of-day sentinel is full-day (a partial closure set to
// exactly those bounds re-opens as full-day, which is harmless — it covers the same span). A partial
// closure keeps a single date taken from startDate; a cross-midnight endDate keeps its time but
// loses its date, matching the single-day window this UI creates.
export const splitClosedDayOccurrence = (occurrence: SingleOccurrence): ClosedDayFormState => {
  const start = occurrence.startDate ?? '';
  const end = occurrence.endDate ?? '';
  const startTime = timeOf(start);
  const endTime = timeOf(end);
  if (isFullDayWindow(startTime, endTime)) {
    return { isFullDay: true, fromDate: dateOf(start), toDate: dateOf(end), startTime: '', endTime: '' };
  }
  const date = dateOf(start);
  return { isFullDay: false, fromDate: date, toDate: date, startTime, endTime };
};

const DATE_RANGE_SEPARATOR = ' – ';

/** The closed period for the list: "2026-12-24", or "2026-12-24 – 2027-01-01" for a multi-day span. */
export const formatClosedDayPeriod = (occurrence: SingleOccurrence): string => {
  const from = dateOf(occurrence.startDate ?? '');
  const to = dateOf(occurrence.endDate ?? '');
  return to && to !== from ? `${from}${DATE_RANGE_SEPARATOR}${to}` : from;
};
